import { sanitizeCustomHtml } from "@redotech/order-tracking-common/sanitize-custom-html";
import { BlockServiceTheme } from "@redotech/redo-block-service-common/messages";
import {
  memo,
  useDeferredValue,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { DRAG_THRESHOLD } from "../builder/constants";
import {
  ISOLATED_BODY_ATTRIBUTE,
  isolatedDocumentHtml,
} from "./custom-html-isolator-document";
import * as css from "./custom-html-isolator.module.css";
import { MAX_FRAME_HEIGHT_PX } from "./runtime-block-height";

/**
 * Sanitizes merchant HTML through the shared allowlist and renders it in its own
 * document, forwarding clicks into it so CSS-only markup still activates.
 */
export const CustomHtmlIsolator = memo(function CustomHtmlIsolator({
  html,
  theme,
}: {
  html: string;
  theme?: BlockServiceTheme;
}) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const pointerDownRef = useRef<{ x: number; y: number } | null>(null);
  // Keeps per-keystroke inspector edits from blocking paint. Sanitizing the
  // deferred value keeps the parse off the urgent render too.
  const deferredHtml = useDeferredValue(html);
  // The same allowlist the server applies, so the merchant previews what ships;
  // only the live customer page executes scripts.
  const sanitizedHtml = useMemo(
    () => sanitizeCustomHtml(deferredHtml),
    [deferredHtml],
  );
  const [reloadCount, setReloadCount] = useState(0);

  useLayoutEffect(() => {
    const iframe = iframeRef.current;
    const iframeDocument = iframe?.contentDocument;
    if (!iframe || !iframeDocument) {
      return;
    }

    // Written rather than set via srcdoc so the doctype lands and the merchant's
    // CSS renders in standards mode instead of quirks mode.
    iframeDocument.open();
    iframeDocument.write(
      isolatedDocumentHtml(wrapperRef.current, sanitizedHtml, theme),
    );
    iframeDocument.close();

    const { body, documentElement } = iframeDocument;
    if (!body) {
      return;
    }
    body.style.overflow = "hidden"; // otherwise an empty scrollbar appears occasionally

    let appliedHeight: number | null = null;

    const applyHeight = () => {
      const height = Math.min(
        documentElement.getBoundingClientRect().height,
        MAX_FRAME_HEIGHT_PX,
      );
      // Skipping sub-pixel deltas is what settles the loop once it hits the cap.
      if (appliedHeight !== null && Math.abs(height - appliedHeight) < 1) {
        return;
      }
      appliedHeight = height;
      iframe.style.height = `${height}px`;
      // The frame can overflow the block's grid box, so the overlay tracks the
      // frame's own height — a wrapper-sized overlay would miss the overflow.
      if (overlayRef.current) {
        overlayRef.current.style.height = `${height}px`;
      }
    };

    applyHeight(); // synchronously, so the UA-default 150px never paints

    let rafId: number | null = null;

    const scheduleHeight = () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
      rafId = requestAnimationFrame(() => {
        if (!documentElement.isConnected) {
          return;
        }
        applyHeight();
      });
    };

    const resizeObserver = new ResizeObserver(scheduleHeight);
    // documentElement, not body: its auto height covers body's own margin box,
    // which merchant CSS inside the frame can set.
    resizeObserver.observe(documentElement);

    return () => {
      resizeObserver.disconnect();
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
    };
  }, [sanitizedHtml, reloadCount, theme]);

  /**
   * A DOM move blanks the frame, and a merchant link can navigate it off-origin
   * where no document is reachable; the key remounts a fresh same-origin one.
   */
  const handleLoad = () => {
    const body = iframeRef.current?.contentDocument?.body;
    if (!body?.hasAttribute(ISOLATED_BODY_ATTRIBUTE)) {
      setReloadCount((count) => count + 1);
    }
  };

  const handleOverlayMouseDown = (event: React.MouseEvent) => {
    pointerDownRef.current = { x: event.clientX, y: event.clientY };
  };

  /**
   * Activates whatever the merchant clicked inside the frame, which the real
   * pointer never enters. Never stops propagation: the block still selects.
   */
  const handleOverlayClick = (event: React.MouseEvent) => {
    const pointerDown = pointerDownRef.current;
    pointerDownRef.current = null;
    // The same threshold the block wrapper uses, so a drag released over the
    // preview lands the block instead of activating markup.
    if (
      pointerDown &&
      (Math.abs(event.clientX - pointerDown.x) > DRAG_THRESHOLD ||
        Math.abs(event.clientY - pointerDown.y) > DRAG_THRESHOLD)
    ) {
      return;
    }

    const iframe = iframeRef.current;
    const frameDocument = iframe?.contentDocument;
    const frameWindow = frameDocument?.defaultView;
    if (!iframe || !frameDocument || !frameWindow) {
      return;
    }

    // The canvas zooms the frame with a CSS transform while its layout viewport
    // stays unscaled; a zero rect (jsdom, display:none) has no scale to undo.
    const rect = iframe.getBoundingClientRect();
    const scaleX =
      rect.width > 0
        ? frameDocument.documentElement.clientWidth / rect.width
        : 1;
    const scaleY =
      rect.height > 0
        ? frameDocument.documentElement.clientHeight / rect.height
        : 1;

    const target = frameDocument.elementFromPoint(
      (event.clientX - rect.left) * scaleX,
      (event.clientY - rect.top) * scaleY,
    );
    // Its own realm, so the frame's HTMLElement is the one it instantiates.
    if (target instanceof frameWindow.HTMLElement) {
      target.click();
    }
  };

  return (
    <div className={css.wrapper} ref={wrapperRef}>
      {/* allow-same-origin without allow-scripts: the parent can write and
      measure the document, merchant scripts still cannot run. */}
      <iframe
        className={css.frame}
        key={reloadCount}
        onLoad={handleLoad}
        ref={iframeRef}
        sandbox="allow-same-origin"
        tabIndex={-1} // the preview is inert to keyboard focus
        title="Custom HTML preview"
      />
      <div
        className={css.overlay}
        onClick={handleOverlayClick}
        onMouseDown={handleOverlayMouseDown}
        ref={overlayRef}
      />
    </div>
  );
});
